/**
 * Workspace Settings — Sprint 1 · Project Symphony + Patch 37.4.9.
 *
 * Sections:
 *   1. Workspace identity (edit display name — owner/admin).
 *   2. Operating mode (Manual ↔ Autopilot).
 *   3. Active campaign cap (owner/admin, integer 1–1000, null = plan default).
 */
import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  useCurrentWorkspace,
  useSetActiveCampaignCap,
  useUpdateWorkspaceProfile,
} from "@/entities/workspace/hooks";
import { OperatingModeToggle } from "@/features/workspace-operating-mode";
import type { Workspace } from "@/entities/workspace/model";
import { BrandAssetsSection } from "./BrandAssetsSection";

export default function WorkspaceSettingsPage(): JSX.Element {
  const workspace = useCurrentWorkspace();

  if (!workspace) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <h1 className="text-2xl font-semibold tracking-tight">Workspace settings</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Select a workspace from the switcher to configure it.
        </p>
      </div>
    );
  }

  return (
    <div
      className="mx-auto max-w-3xl p-6 sm:p-8 space-y-8"
      data-testid="workspace-settings-page"
    >
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          Workspace settings
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure how <span className="font-medium">{workspace.profile.display_name}</span> works.
        </p>
      </header>

      <WorkspaceIdentitySection workspace={workspace} />
      <BrandAssetsSection workspaceId={workspace.id} />
      <OperatingModeToggle workspace={workspace} />
      <CampaignCapSection workspace={workspace} />
    </div>
  );
}

/* ─── Identity ──────────────────────────────────────────────────── */

function WorkspaceIdentitySection({ workspace }: { workspace: Workspace }): JSX.Element {
  const [displayName, setDisplayName] = useState(workspace.profile.display_name);
  const [tagline, setTagline] = useState(workspace.profile.tagline ?? "");
  const mutation = useUpdateWorkspaceProfile(workspace.id);

  const dirty =
    displayName.trim() !== workspace.profile.display_name ||
    (tagline || "").trim() !== (workspace.profile.tagline ?? "");
  const valid = displayName.trim().length >= 1 && displayName.trim().length <= 80;

  const save = () => {
    if (!dirty || !valid || mutation.isPending) return;
    mutation.mutate(
      {
        ...workspace.profile,
        display_name: displayName.trim(),
        tagline: tagline.trim() || null,
      },
      {
        onSuccess: () => toast.success("Workspace updated"),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : "Could not update workspace"),
      },
    );
  };

  return (
    <Card className="p-6" data-testid="settings-identity">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Workspace identity</h2>
        <p className="text-xs text-muted-foreground">
          Owners and admins can rename the workspace. This is what appears in the top bar,
          on shared campaign links, and on generated assets.
        </p>
      </div>
      <div className="space-y-4">
        <div>
          <Label htmlFor="ws-display-name">Display name</Label>
          <Input
            id="ws-display-name"
            data-testid="settings-display-name-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={80}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="ws-tagline">Tagline (optional)</Label>
          <Input
            id="ws-tagline"
            data-testid="settings-tagline-input"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            maxLength={200}
            placeholder="One line, shown under the workspace name."
            className="mt-1"
          />
        </div>
        <div className="flex justify-end">
          <Button
            data-testid="settings-save-identity"
            onClick={save}
            disabled={!dirty || !valid || mutation.isPending}
          >
            {mutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save changes
          </Button>
        </div>
      </div>
    </Card>
  );
}

/* ─── Active campaign cap ───────────────────────────────────────── */

const PLAN_DEFAULT_CAP = 10;

function CampaignCapSection({ workspace }: { workspace: Workspace }): JSX.Element {
  const stored = workspace.settings.active_campaign_cap ?? null;
  const [override, setOverride] = useState<string>(stored != null ? String(stored) : "");
  const mutation = useSetActiveCampaignCap(workspace.id);

  const parsed = override.trim() === "" ? null : Number.parseInt(override, 10);
  const valid = parsed === null || (Number.isFinite(parsed) && parsed >= 1 && parsed <= 1000);
  const dirty = parsed !== stored;

  const save = () => {
    if (!dirty || !valid || mutation.isPending) return;
    mutation.mutate(parsed, {
      onSuccess: (updated) => {
        const nextCap = updated.settings.active_campaign_cap ?? null;
        toast.success(
          nextCap == null
            ? `Active campaign cap reset to the plan default (${PLAN_DEFAULT_CAP}).`
            : `Active campaign cap set to ${nextCap}.`,
        );
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : "Could not update the cap"),
    });
  };

  const effective = stored ?? PLAN_DEFAULT_CAP;

  return (
    <Card className="p-6" data-testid="settings-campaign-cap">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Active campaign cap</h2>
        <p className="text-xs text-muted-foreground">
          The maximum number of concurrent draft / planned / executing campaigns this
          workspace can hold. Leave empty to fall back to the plan default
          (<span className="font-medium">{PLAN_DEFAULT_CAP}</span>). Applies to Campaign Commander.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr,auto,auto] sm:items-end">
        <div>
          <Label htmlFor="ws-cap-input">Override</Label>
          <Input
            id="ws-cap-input"
            data-testid="settings-campaign-cap-input"
            inputMode="numeric"
            pattern="[0-9]*"
            value={override}
            onChange={(e) => setOverride(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder={`Plan default (${PLAN_DEFAULT_CAP})`}
            className="mt-1"
          />
          {!valid ? (
            <p className="mt-1 text-xs text-rose-500">Enter a number between 1 and 1000, or leave blank.</p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              Effective cap right now: <span className="font-medium">{effective}</span>
              {stored == null ? " (plan default)" : " (workspace override)"}.
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          data-testid="settings-clear-campaign-cap"
          onClick={() => setOverride("")}
          disabled={override === "" || mutation.isPending}
        >
          Clear
        </Button>
        <Button
          data-testid="settings-save-campaign-cap"
          onClick={save}
          disabled={!dirty || !valid || mutation.isPending}
        >
          {mutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save cap
        </Button>
      </div>
    </Card>
  );
}
